"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeResource = void 0;
const lodash_merge_1 = __importDefault(require("lodash.merge"));
const request_1 = require("./request");
const utils_1 = require("./utils");
/**
 * Creates a new Resource class definition for the user to extend.
 *
 * @export
 * @example
 * ```js
 * class Pokemon extends makeResource<Pokemon>('/api/pokemon') {
 *  public id?: number;
 *  public name!: string;
 *  public types!: string[];
 * }
 * ```
 *
 * @template T The return type of the resource.
 * @template ID The type for the id field of the resource defaults to number.
 * @param {string} baseUrl The baseUrl of the resource
 * @returns
 *
 * @deprecated A resource provides basic functions that might not be defined in
 * your API, and detail responses often provide more/other properties, which
 * could cause type confusion or unnecessary checks for optional properties.
 * We recommend using explicit functions for calls to your API that provide
 * specific typing.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function makeResource(config) {
    const baseUrl = typeof config === 'string' ? config : config.baseUrl;
    const mapper = getMapper(config);
    /**
     *  A Resource represents an Object which is returned from a REST API.
     * It knows how to save / update itself, and various means to retrieve
     * the Resource from the server.
     */
    return class {
        /**
         * Either creates a new Resource by performing a POST when the id is
         * empty, or updates an existing resource via a PUT request when the
         * id exists.
         *
         * @example
         * ```js
         * pokemon = new Pokemon();
         * pokemon.name = "bulbasaur";
         * pokemon.save();
         * ```
         *
         * @returns {Promise<T>} The instance updated with the id from the back-end.
         */
        save() {
            return __awaiter(this, void 0, void 0, function* () {
                let json;
                if (this.id) {
                    // @ts-expect-error Type of itself is always T
                    json = yield (0, request_1.put)(`${baseUrl}/${this.id}`, this);
                }
                else {
                    // @ts-expect-error Type of itself is always T
                    json = yield (0, request_1.post)(`${baseUrl}`, this);
                }
                return (0, lodash_merge_1.default)(this, json);
            });
        }
        /**
         * Removes an existing Resource by performing a DELETE request.
         *
         * @example
         * ```js
         * const pokemon = Pokemon.one(1);
         * pokemon.remove().then(() => {
         *   // The pokemon is now removed from the back-end..
         * });
         * ```
         *
         * @returns {Promise<T>} The instance which was removed, but with an empty id.
         * @throws {Error} When the id is not defined it throws an error.
         */
        remove() {
            return __awaiter(this, void 0, void 0, function* () {
                if (this.id) {
                    yield (0, request_1.remove)(`${baseUrl}/${this.id}`);
                    this.id = undefined;
                    // @ts-expect-error accept `this` as T
                    return this;
                }
                else {
                    throw new Error('Cannot remove a Resource which has no id, this is a programmer error.');
                }
            });
        }
        /**
         * Retrieves a single Resource of type T from the server.
         *
         * @example
         * ```js
         * Pokemon.get(1).then((pokemon) => {
         *   console.log(pokemon);
         * });
         * ```
         *
         * @static
         * @param {number | string} id The id of the Resource you want to retrieve
         * @param {QueryParams} queryParams Optional query params for the url
         * @returns {Promise<T>} A Promise returning the Resource of type T.
         *
         */
        static one(id, queryParams) {
            return __awaiter(this, void 0, void 0, function* () {
                const json = yield (0, request_1.get)(`${baseUrl}/${id}`, queryParams);
                // @ts-expect-error accept `this` as T
                return mapper(json, this);
            });
        }
        /**
         * Find a single optional Resource of type T from the server.
         *
         * @example
         * ```js
         * Pokemon.findOne({ name: 'Pikachu' }).then((pokemon) => {
         *   console.log(pokemon);
         * });
         * ```
         *
         * @static
         * @param {QueryParams} queryParams The query params for the url
         * @returns {Promise<T | void>} A Promise returning the Resource of type T or undefined.
         */
        static findOne(queryParams) {
            return __awaiter(this, void 0, void 0, function* () {
                const json = yield (0, request_1.get)(baseUrl, queryParams);
                if (typeof json !== 'object' ||
                    json === null ||
                    Array.isArray(json) ||
                    Object.keys(json).length === 0) {
                    return undefined;
                }
                // @ts-expect-error accept `this` as T
                return mapper(json, this);
            });
        }
        /**
         * Retrieves multiple Resources of type T from the server as an Array.
         *
         * @example
         * ```js
         * Pokemon.list().then((pokemon: Array<Pokemon>) => {
         *   console.log(pokemon);
         * });
         * ```
         *
         * @static
         * @param {QueryParams} queryParams Optional query params for the url
         * @returns {Promise<Array<T>>} A Promise returning the Resource of type Array<T>.
         *
         * @memberOf Resource
         */
        static list(queryParams) {
            return __awaiter(this, void 0, void 0, function* () {
                const list = yield (0, request_1.get)(baseUrl, queryParams);
                return list.map((properties) => {
                    // @ts-expect-error accept `this` as T
                    return mapper(properties, this);
                });
            });
        }
        /**
         * Retrieves multiple Resources of type T from the server as a Page.
         *
         * @example
         * ```js
         * Pokemon.page({ page: 1 }).then((pokemon: Page<Pokemon>) => {
         *   console.log(pokemon);
         * });
         * ```
         *
         * @static
         * @param {QueryParams} queryParams Optional query params for the url
         * @returns {Promise<Page<T>>} A Promise returning the Resource of type Page<T>.
         */
        static page(queryParams) {
            return __awaiter(this, void 0, void 0, function* () {
                const page = yield (0, request_1.get)(baseUrl, queryParams);
                page.content = page.content.map((properties) => {
                    // @ts-expect-error accept `this` as T
                    return mapper(properties, this);
                });
                return page;
            });
        }
    };
}
exports.makeResource = makeResource;
/**
 * Returns the mapper to be used for the Resource based on the config.
 */
function getMapper(config) {
    if (typeof config === 'string') {
        return defaultMapper;
    }
    else {
        return config.mapper ? config.mapper : defaultMapper;
    }
}
/**
 * The default mapper uses `makeInstance` to convert the JSON
 * to a T.
 */
function defaultMapper(json, Class) {
    return (0, utils_1.makeInstance)(Class, json);
}
